import { PropsWithChildren } from 'react';
import { compact } from 'lodash';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ChevronDown16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';

import { StyledActionMenuButton } from './Styled';
import { useTranslation } from '../../hooks';
import { useExportDiagnosticInfo } from '../../hooks/useExportDiagnositcInfo';
import { useListDetailsContext } from '../../contexts/listDetails';
import { useModalsContext } from '../../contexts/modals';
import { usePermissionsContext } from '../../contexts/permissions';
import { useSegmentationQueryContext } from '../../contexts/segmentationQuery';
import { FORM_ID, PAGE_VARIATIONS } from '../../constants';
import { SubmitType } from '../../types';
import { useIsCatalogSaveRestricted } from '../../hooks/useIsCatalogSaveRestricted';

export const testIds = {
  actBtnCSV: 'segmentation-sam-act-btn-csv',
  actBtnDynamic: 'segmentation-sam-act-btn-dynamic',
  actBtnFacebook: 'segmentation-sam-act-btn-facebook',
  actBtnStatic: 'segmentation-sam-act-btn-static',
  actMenuSave: 'segmentation-sam-act-menu-save',
  actTtlCreateNew: 'segmentation-sam-act-ttl-create-new',
  actTtlExportAs: 'segmentation-sam-act-ttl-export-as',
  actBtnDiagnostic: 'segmentation-sam-act-btn-export-diagonstics',
};

export const SaveActionMenu = ({
  variant = BUTTON_VARIANTS.SECONDARY,
}: { variant?: BUTTON_VARIANTS }) => {
  const { t } = useTranslation();
  const { pageVariation } = useListDetailsContext();
  const {
    canExportDiagnosticInformation,
    canManageSegmentation,
    canViewAndExpUserData,
    hasFacebookIntegration,
  } = usePermissionsContext();
  const showCreateDynamicList = pageVariation !== PAGE_VARIATIONS.STATIC;
  const {
    showExportToCSVModal,
    showExportToFBCAModal,
    showNewDynamicListModal,
    showNewStaticListsModal,
  } = useModalsContext();
  const { isValidQuery } = useSegmentationQueryContext();
  const exportDiagnosticInformation = useExportDiagnosticInfo();

  const isCatalogSaveRestricted = useIsCatalogSaveRestricted();

  const tooltipContent = !canManageSegmentation
    ? 'permissions.manageSegmentation'
    : 'actionMenu.disabledByCatalogQuery';

  const isDynamicListDisabled = !canManageSegmentation || isCatalogSaveRestricted;

  const options: ActionMenuOption[] = compact([
    {
      as: AllowedActionMenuElements.Title,
      content: t('actionMenu.save.titleCreateNew'),
      id: testIds.actTtlCreateNew,
    },
    showCreateDynamicList && {
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.save.btnDynamic'),
      id: testIds.actBtnDynamic,
      isDisabled: isDynamicListDisabled,
      onClick: showNewDynamicListModal,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip isEnabled={isDynamicListDisabled} tooltipContent={t(tooltipContent)}>
          {children}
        </Tooltip>
      ),
    },
    {
      appendDivider: true,
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.save.btnStatic'),
      id: testIds.actBtnStatic,
      isDisabled: !canManageSegmentation,
      onClick: showNewStaticListsModal,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!canManageSegmentation}
          tooltipContent={t(`permissions.manageSegmentation`)}
        >
          {children}
        </Tooltip>
      ),
    },
    {
      as: AllowedActionMenuElements.Title,
      content: t('actionMenu.save.titleExportAs'),
      id: testIds.actTtlExportAs,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.save.btnCSV'),
      id: testIds.actBtnCSV,
      isDisabled: !canViewAndExpUserData,
      onClick: showExportToCSVModal,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!canViewAndExpUserData}
          tooltipContent={t(`permissions.viewAndExportUserData`)}
        >
          {children}
        </Tooltip>
      ),
    },
    {
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.save.btnFacebook'),
      id: testIds.actBtnFacebook,
      isDisabled: !hasFacebookIntegration || !canViewAndExpUserData,
      onClick: showExportToFBCAModal,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!hasFacebookIntegration || !canViewAndExpUserData}
          tooltipContent={t(
            `permissions.${
              !canViewAndExpUserData ? 'viewAndExportUserData' : 'noFacebookAdAccounts'
            }`,
          )}
        >
          {children}
        </Tooltip>
      ),
    },
    canExportDiagnosticInformation && {
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.save.btnDiagnostic'),
      id: testIds.actBtnDiagnostic,
      onClick: exportDiagnosticInformation,
    },
  ]);

  // Differenates bbetween the variant in the side panel and the one in the filter header
  const actionMenuTestId = `${testIds.actMenuSave}${variant !== BUTTON_VARIANTS.SECONDARY ? `-${variant}` : ''}`;

  return (
    <Tooltip isEnabled={!isValidQuery} tooltipContent={t('actionMenu.disabled')}>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        data-test={actionMenuTestId}
        options={options}
        isDisabled={!isValidQuery}
        style={{ width: '100%' }}
      >
        <StyledActionMenuButton
          variant={variant}
          size={BUTTON_SIZES.SMALL}
          type="submit"
          form={FORM_ID}
          id={SubmitType.save}
        >
          <span>{t('actionMenu.save.label')}</span>
          <ChevronDown16 />
        </StyledActionMenuButton>
      </ActionMenu>
    </Tooltip>
  );
};
